import React, { useEffect, useRef, useState } from 'react';
import { CloseIcon } from '../common/icon';
import { Tracker } from '@/utils';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';

const DOWNLOAD_APP_BANNER_HIDE_KEY = 'download_app_banner_hide_until';
const DOWNLOAD_APP_BANNER_VIEW_REPORTED_KEY_PREFIX =
  'download_app_banner_view_reported_';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const PLAY_STORE_PACKAGE = 'io.aha.creator.webview';

export default function DownloadAppBanner({
  className,
  source = 'outside',
  menuOpen = false
}: {
  className?: string;
  source?: 'outside' | 'navbar';
  menuOpen?: boolean;
}) {
  const { t } = useTranslation();
  const isNavbarSource = source === 'navbar';
  const hasReportedViewRef = useRef(false);
  const { user } = useUser();

  const buildBannerAttributionPlayStoreUrl = ({
    bannerSource
  }: {
    bannerSource: 'outside' | 'navbar';
  }) => {
    const clickId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const referrer = new URLSearchParams({
      utm_source: 'download_app_banner',
      utm_medium: 'app_banner',
      utm_campaign: 'app_download',
      banner_source: bannerSource,
      banner_id: 'download_app_banner',
      click_id: clickId,
      user_id: user?.id || ''
    }).toString();
    const url = `https://play.google.com/store/apps/details?id=${PLAY_STORE_PACKAGE}&referrer=${encodeURIComponent(referrer)}`;
    return { url, clickId };
  };

  const [isShow, setIsShow] = useState(() => {
    if (isNavbarSource) {
      return true;
    }
    try {
      const hideUntil = Number(
        localStorage.getItem(DOWNLOAD_APP_BANNER_HIDE_KEY)
      );
      return !hideUntil || Number.isNaN(hideUntil) || Date.now() >= hideUntil;
    } catch (error) {
      return true;
    }
  });

  const handleClose = (e: React.MouseEvent<HTMLDivElement>) => {
    Tracker.click('download_app_banner_close', 1, 'download_app_banner');
    e.stopPropagation();
    e.preventDefault();
    try {
      localStorage.setItem(
        DOWNLOAD_APP_BANNER_HIDE_KEY,
        String(Date.now() + SEVEN_DAYS_MS)
      );
    } catch (error) {
      // ignore storage errors and still hide for current session
    }
    setIsShow(false);
  };

  useEffect(() => {
    if (!isShow || hasReportedViewRef.current) return;
    if (isNavbarSource && !menuOpen) return;
    const storageKey = `${DOWNLOAD_APP_BANNER_VIEW_REPORTED_KEY_PREFIX}${source}`;
    try {
      if (sessionStorage.getItem(storageKey) === '1') {
        hasReportedViewRef.current = true;
        return;
      }
    } catch (error) {
      // ignore sessionStorage errors
    }

    hasReportedViewRef.current = true;
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch (error) {
      // ignore sessionStorage errors
    }
    Tracker.gtag('event', 'screen_view', {
      event_category: 'download_app_banner_view',
      utm_source: source,
      utm_medium: 'app_banner',
      value: isNavbarSource ? 1 : 2
    });
  }, [isShow, menuOpen, source, isNavbarSource]);

  if (!isShow) return null;
  return (
    <div
      onClick={() => {
        const { url, clickId } = buildBannerAttributionPlayStoreUrl({
          bannerSource: source
        });
        Tracker.gtag('event', 'select_content', {
          event_category: 'download_app_banner_click',
          utm_source: source,
          utm_medium: 'app_banner',
          utm_campaign: 'app_download',
          banner_id: 'download_app_banner',
          click_id: clickId
        });
        window.open(url, '_blank');
      }}
      className={`relative w-full p-4 flex items-center gap-3 bg-[radial-gradient(74.74%_219.64%_at_18.75%_233.93%,_#CFAFFF_0%,_rgba(207,_175,_255,_0)_100%),_radial-gradient(40.22%_80.36%_at_50%_112.5%,_rgba(142,_225,_255,_0.5)_0%,_rgba(142,_225,_255,_0)_100%),_linear-gradient(0deg,_rgba(255,_255,_255,_0.4),_rgba(255,_255,_255,_0.4))] md:hidden ${isNavbarSource ? 'justify-between rounded-[16px]' : ''} ${className}`}
    >
      <div className="flex flex-col justify-start items-start">
        <div className="self-stretch justify-start text-[#1C1917] text-base font-bold font-['DM_Sans'] leading-6">
          {t('downloadAppBanner.title', {
            defaultValue: 'Get the AhaCreator App'
          })}
        </div>
        <div className="self-stretch justify-start text-[#57534E] text-xs font-normal font-['DM_Sans'] leading-4">
          {t('downloadAppBanner.subtitle', {
            defaultValue: 'Instant collab alerts • Live order updates'
          })}
        </div>
      </div>
      <div className="rounded-md bg-[#1C1917] px-3 h-[34px] flex items-center justify-center">
        <div className="justify-start text-white text-sm font-semibold font-['DM_Sans'] leading-5">
          {t('downloadAppBanner.download', {
            defaultValue: 'Download'
          })}
        </div>
      </div>
      {!isNavbarSource && (
        <div
          className="absolute top-0 right-[6px] w-[20px] h-[20px]"
          onClick={handleClose}
        >
          <CloseIcon className="text-[#44403C] w-4 h-4" />
        </div>
      )}
    </div>
  );
}
